/**
 * Hook for evaluating conditional field visibility, required state, and
 * disabled state in EntityForm.
 *
 * Calls `evaluateConditions()` from the condition evaluator for each field
 * that has conditions defined, memoises the results keyed on form values
 * and field configs, and exposes convenience accessors for the rest of the
 * form system.
 *
 * Additionally handles the `setValue` side-effect described in spec 6.2
 * rule 6: a condition's `effect.setValue` is applied only the first time
 * that condition becomes active, and not re-applied on subsequent renders
 * while the condition remains true.
 *
 * And the rule overlay's `effect.checked` (spec 6.4): ticks / unticks the
 * target checkbox on the predicate's false → true edge caused by an edit —
 * never on the initial evaluation nor on a bulk load, never reverted.
 *
 * @see Spec sections 4.2, 6.1, 6.2, 6.4
 * @see Plan section 3.3
 */

import * as React from 'react';

import {
  applyRuleConditions,
  evaluateConditions,
  evaluateRuleCheckedStates,
} from '../utils/conditionEvaluator';
import type { ConditionEvaluationResult } from '../utils/conditionEvaluator';
import type { FieldCondition, FieldConfig } from '../types';

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

/**
 * Shape returned by `useFieldConditions`.
 */
export interface UseFieldConditionsReturn {
  /** Check whether a specific field is currently visible. */
  isFieldVisible: (name: string) => boolean;

  /** Check whether a specific field is currently required. */
  isFieldRequired: (name: string) => boolean;

  /** Check whether a specific field is currently disabled. */
  isFieldDisabled: (name: string) => boolean;

  /**
   * Map of field name to its current visibility state.
   * Useful when other hooks need the full map (e.g. validation skips
   * hidden fields).
   */
  visibilityMap: Record<string, boolean>;

  /**
   * Map of field name to its current required state (base config +
   * conditions + rule overlay). Validation folds it into the schema so a
   * field made required at runtime is validated, not just asterisked.
   */
  requiredMap: Record<string, boolean>;

  /**
   * All fields with their evaluated `visible` and `required` states merged
   * onto the base config.
   */
  resolvedFields: Record<string, FieldConfig & { visible: boolean; required: boolean }>;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Builds a stable serialisation key from a condition-evaluation result map
 * that can be used for the `previouslyActive` ref comparison.
 *
 * A condition's setValue should fire when the condition *first becomes true*.
 * We track this by building a composite key per field of the form
 * `"fieldName:setValue:value"` so we can detect the transition.
 */
function buildSetValueKey(fieldName: string, value: unknown): string {
  return `${fieldName}::${JSON.stringify(value)}`;
}

/**
 * Returns `true` when the supplied value is a non-empty condition tree that
 * should be passed to `evaluateConditions`.
 *
 * Accepts both the legacy array shape (non-empty means length > 0) and the
 * single-node shape introduced in the phase-2 backport (a
 * {@link ConditionGroup} or a {@link SimpleCondition} is always considered
 * present, although empty groups are tolerated by the evaluator).
 */
function hasConditions(
  conditions: FieldCondition | FieldCondition[] | undefined,
): conditions is FieldCondition | FieldCondition[] {
  if (conditions === undefined) return false;
  if (Array.isArray(conditions)) return conditions.length > 0;
  return true;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Evaluates conditional field logic for every field in the config.
 *
 * @param fields  - The field configurations keyed by field name.
 * @param values  - Current form values keyed by field name.
 * @param onSetValue - Optional callback invoked when a condition's
 *   `effect.setValue` first becomes active, or a rule overlay entry's
 *   `effect.checked` hits its rising edge. Typically wired to the form's
 *   `setValue` function.
 * @param loadGeneration - Optional counter that changes whenever `values` were
 *   bulk-loaded rather than edited (see `useFormValues`' `loadGeneration`). A
 *   change re-baselines the `checked` edge detector instead of firing it.
 *   Omitted ⇒ only the initial evaluation is treated as a load.
 *
 * @returns Condition evaluation accessors and maps.
 */
export function useFieldConditions(
  fields: Record<string, FieldConfig>,
  values: Record<string, unknown>,
  onSetValue?: (fieldName: string, value: unknown) => void,
  loadGeneration = 0,
): UseFieldConditionsReturn {
  // -----------------------------------------------------------------------
  // Ref: track which setValue conditions have already fired
  // -----------------------------------------------------------------------

  /**
   * Keys that are currently "active" -- i.e. the condition is true and
   * the setValue has already been dispatched. We use a Set of composite
   * keys (`fieldName::serialisedValue`) so we can detect the transition
   * from inactive -> active and only fire `onSetValue` once.
   */
  const previouslyActiveRef = React.useRef<Set<string>>(new Set());

  // -----------------------------------------------------------------------
  // Memoised condition evaluation
  // -----------------------------------------------------------------------

  /**
   * Evaluate all conditions for every field whenever `values` or `fields`
   * change. Returns a map of field name -> ConditionEvaluationResult.
   *
   * The field-level `conditions` property accepts either the legacy
   * {@link FieldCondition}`[]` shape (implicit AND) or — thanks to the
   * phase-2 form-rules backport — a {@link ConditionGroup} wrapper embedded
   * in that array for explicit AND/OR nesting. Both shapes flow through
   * `evaluateConditions` unchanged; the evaluator handles the dispatch.
   *
   * The rule-injected overlay (`FieldConfig.ruleConditions`, produced by a
   * `setFieldCondition` component-rule effect) is applied on top of that base
   * result for EVERY field — with or without base conditions — via
   * `applyRuleConditions`. It is a no-op (same object) when the field carries
   * no overlay or no overlay entry currently matches.
   */
  const evaluationMap = React.useMemo<
    Record<string, ConditionEvaluationResult>
  >(() => {
    const result: Record<string, ConditionEvaluationResult> = {};

    for (const [fieldName, fieldConfig] of Object.entries(fields)) {
      const base = hasConditions(fieldConfig.conditions)
        ? evaluateConditions(fieldConfig.conditions, values, fieldConfig)
        : // No conditions -- derive defaults from the base config.
          {
            visible: fieldConfig.visible !== false,
            required:
              fieldConfig.validation?.required === true ||
              typeof fieldConfig.validation?.required === 'string',
            disabled: fieldConfig.disabled === true,
          };

      result[fieldName] = applyRuleConditions(
        base,
        fieldConfig.ruleConditions,
        values,
      );
    }

    return result;
  }, [fields, values]);

  // -----------------------------------------------------------------------
  // Derived maps (memoised)
  // -----------------------------------------------------------------------

  /** Map of field name -> boolean visibility. */
  const visibilityMap = React.useMemo<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const [fieldName, evaluation] of Object.entries(evaluationMap)) {
      map[fieldName] = evaluation.visible;
    }
    return map;
  }, [evaluationMap]);

  /** Map of field name -> boolean required state. */
  const requiredMap = React.useMemo<Record<string, boolean>>(() => {
    const map: Record<string, boolean> = {};
    for (const [fieldName, evaluation] of Object.entries(evaluationMap)) {
      map[fieldName] = evaluation.required;
    }
    return map;
  }, [evaluationMap]);

  /** Resolved fields: base config merged with evaluated states. */
  const resolvedFields = React.useMemo<
    Record<string, FieldConfig & { visible: boolean; required: boolean }>
  >(() => {
    const map: Record<
      string,
      FieldConfig & { visible: boolean; required: boolean }
    > = {};

    for (const [fieldName, fieldConfig] of Object.entries(fields)) {
      const evaluation = evaluationMap[fieldName];
      if (evaluation) {
        map[fieldName] = {
          ...fieldConfig,
          visible: evaluation.visible,
          required: evaluation.required,
          // Merge disabled state from evaluation
          disabled: evaluation.disabled,
          // Merge options override from evaluation when present
          ...(evaluation.options ? { options: evaluation.options } : {}),
        };
      }
    }

    return map;
  }, [fields, evaluationMap]);

  // -----------------------------------------------------------------------
  // setValue side-effect
  // -----------------------------------------------------------------------

  /**
   * When a condition's `effect.setValue` first becomes true, invoke the
   * callback exactly once. On subsequent renders where the condition
   * remains true, do nothing. When the condition becomes false and then
   * true again, fire once more.
   *
   * Spec 6.2, rule 6: "setValue in conditions only applies when the
   * condition first becomes true (not on every render)."
   */
  React.useEffect(() => {
    if (!onSetValue) return;

    const nextActive = new Set<string>();

    for (const [fieldName, evaluation] of Object.entries(evaluationMap)) {
      if (evaluation.setValue !== undefined) {
        const key = buildSetValueKey(fieldName, evaluation.setValue);
        nextActive.add(key);

        // Fire only on the inactive -> active transition
        if (!previouslyActiveRef.current.has(key)) {
          onSetValue(fieldName, evaluation.setValue);
        }
      }
    }

    // Update the ref so we know what's active for the next render cycle
    previouslyActiveRef.current = nextActive;
  }, [evaluationMap, onSetValue]);

  // -----------------------------------------------------------------------
  // `checked` side-effect (rule overlay) — edge-triggered value write
  // -----------------------------------------------------------------------

  /**
   * Last observed predicate state of every `checked`-carrying overlay entry,
   * keyed `fieldName::<entry key>`. `null` until the first run — the mount
   * baseline.
   */
  const checkedStatesRef = React.useRef<Map<string, boolean> | null>(null);

  /** The `loadGeneration` the states above were recorded under. */
  const checkedGenerationRef = React.useRef(loadGeneration);

  /**
   * A rule overlay entry carrying `effect.checked` writes that value to its
   * target field exactly when its predicate goes false → true because of an
   * edit. Deliberately unlike `setValue` above, it does NOT fire when the
   * predicate is already true on the first evaluation or right after a bulk
   * load (seed, `reset`, `setValues`): those record a new baseline instead,
   * so a loaded record's persisted value is never overwritten. An entry seen
   * for the first time (rules that arrive after mount, a rule edited in the
   * admin preview) also only records its baseline. Nothing happens on the
   * true → false edge (no revert), and the user may toggle the box freely
   * while the predicate stays true.
   */
  React.useEffect(() => {
    const previous = checkedStatesRef.current;
    const rebaseline =
      previous === null || loadGeneration !== checkedGenerationRef.current;
    checkedGenerationRef.current = loadGeneration;

    const next = new Map<string, boolean>();
    // Field -> value to write. Rising entries apply in array order, so the
    // last one on a field wins (the overlay's "last matching wins").
    const writes = new Map<string, boolean>();

    for (const [fieldName, fieldConfig] of Object.entries(fields)) {
      for (const state of evaluateRuleCheckedStates(
        fieldConfig.ruleConditions,
        values,
      )) {
        const key = `${fieldName}::${state.key}`;
        next.set(key, state.active);
        if (!rebaseline && state.active && previous.get(key) === false) {
          writes.set(fieldName, state.checked);
        }
      }
    }

    checkedStatesRef.current = next;

    if (!onSetValue) return;
    for (const [fieldName, checked] of writes) {
      // Already in the requested state — skip the no-op store write.
      if (values[fieldName] !== checked) {
        onSetValue(fieldName, checked);
      }
    }
  }, [fields, values, loadGeneration, onSetValue]);

  // -----------------------------------------------------------------------
  // Accessor callbacks (stable references via useCallback)
  // -----------------------------------------------------------------------

  const isFieldVisible = React.useCallback(
    (name: string): boolean => {
      // If the field is not in the evaluation map (unknown field), default
      // to visible so it doesn't silently disappear.
      return evaluationMap[name]?.visible ?? true;
    },
    [evaluationMap],
  );

  const isFieldRequired = React.useCallback(
    (name: string): boolean => {
      return evaluationMap[name]?.required ?? false;
    },
    [evaluationMap],
  );

  const isFieldDisabled = React.useCallback(
    (name: string): boolean => {
      return evaluationMap[name]?.disabled ?? false;
    },
    [evaluationMap],
  );

  // -----------------------------------------------------------------------
  // Return
  // -----------------------------------------------------------------------
  // Memoised so the whole return object is referentially stable and only
  // changes when an underlying member changes. `useEntityForm` lists this
  // object wholesale in dependency arrays, so an unstable literal would
  // defeat its memoisation.

  return React.useMemo<UseFieldConditionsReturn>(
    () => ({
      isFieldVisible,
      isFieldRequired,
      isFieldDisabled,
      visibilityMap,
      requiredMap,
      resolvedFields,
    }),
    [
      isFieldVisible,
      isFieldRequired,
      isFieldDisabled,
      visibilityMap,
      requiredMap,
      resolvedFields,
    ],
  );
}
